import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../auth/msalConfig';
import {
  MdClose,
  MdAdd,
  MdSave,
  MdRefresh,
  MdSearch,
  MdDeleteOutline,
  MdArrowUpward,
  MdArrowDownward,
  MdCheckCircle,
  MdWarningAmber,
} from '../../utils/icons';
import { unwrapEnvelope, StepApiError, fetchWithRetry, TRANSIENT_STATUS, TRANSIENT_MESSAGE } from './stepApi';
import './AttributeConfig.css';

// ── Spec ───────────────────────────────────────────────────────────────────
// "Attribute List / Prompt Configuration" screen. Maintains the global STEP
// attribute list the extraction workflow injects into the LLM prompt.
//   GET {base}   -> { ok:true, data:{ version, count, rows } }
//   PUT {base}   -> { ok:true, data:{ saved, version } }   (replace the whole list)
// PUT is replace-semantics: always send the full list. Send base_version — a stale
// value is now rejected with 409 VERSION_CONFLICT (details.current_version).

// ── Types ────────────────────────────────────────────────────────────────────

// Editable in-memory row. `_key` is a stable local id (step_id can be edited /
// duplicated mid-session, so it can't key React rows).
interface AttrRow {
  _key: string;
  step_id: string;
  mapping_guidance: string;
  example_value: string | null;
  enabled: boolean;
}

// Server row shape (GET response `rows[]`).
interface ServerRow {
  id?: number;
  step_id?: string;
  mapping_guidance?: string;
  example_value?: string | null;
  enabled?: boolean;
  sort_order?: number;
  opco?: string | null;
  version?: number;
}

interface AttributeConfigProps {
  open: boolean;
  onClose: () => void;
  updatedBy: string;
}

// ── Validation (mirror of server rules — §3) ──────────────────────────────────

const STEP_ID_RE = /^[A-Za-z0-9_.-]+$/;

interface Invalid { rowKey?: string; message: string; }

// Returns the first violation, or null when the set is valid.
const validate = (rows: AttrRow[]): Invalid | null => {
  if (rows.length === 0) {
    return { message: 'List is empty — add at least one attribute before saving.' };
  }
  const seen = new Set<string>();
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const id = r.step_id.trim();
    if (!STEP_ID_RE.test(id)) {
      return { rowKey: r._key, message: `Invalid STEP ID at row ${i + 1}: "${r.step_id}".` };
    }
    if (seen.has(id)) {
      return { rowKey: r._key, message: `Duplicate STEP ID at row ${i + 1}: "${id}".` };
    }
    seen.add(id);
    if (r.mapping_guidance.trim() === '') {
      return { rowKey: r._key, message: `Mapping Guidance is empty at row ${i + 1} ("${id}").` };
    }
  }
  return null;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

let keySeq = 0;
const nextKey = (): string => `row-${++keySeq}`;

const toAttrRow = (r: ServerRow): AttrRow => ({
  _key: nextKey(),
  step_id: typeof r.step_id === 'string' ? r.step_id : '',
  mapping_guidance: typeof r.mapping_guidance === 'string' ? r.mapping_guidance : '',
  example_value: typeof r.example_value === 'string' ? r.example_value : null,
  enabled: r.enabled !== false,
});

// PUT row — sort_order derived from grid position (§2.2).
const toServerRow = (r: AttrRow, index: number) => ({
  step_id: r.step_id.trim(),
  mapping_guidance: r.mapping_guidance.trim(),
  example_value: r.example_value && r.example_value.trim() !== '' ? r.example_value.trim() : null,
  enabled: r.enabled,
  sort_order: (index + 1) * 10,
});

// ── Component ────────────────────────────────────────────────────────────────

const AttributeConfig: React.FC<AttributeConfigProps> = ({ open, onClose, updatedBy }) => {
  const configUrl = process.env.REACT_APP_PRODUCT_AGENT_WEBHOOK_URL + '/webhook/step-attribute-config';

  const { instance, accounts } = useMsal();

  // Acquire an AAD access token for the API scope. APIM rejects requests without
  // it (401 Unauthorized). Returns null when a redirect was triggered (silent
  // acquisition failed) — caller must bail out.
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch {
      await instance.acquireTokenRedirect(apiRequest);
      return null;
    }
  }, [instance, accounts]);

  const [rows, setRows] = useState<AttrRow[]>([]);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<Invalid | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [search, setSearch] = useState('');
  // Snapshot of the loaded set — drives the dirty indicator + Discard.
  const [pristine, setPristine] = useState<string>('[]');

  // Serialise the editable surface so dirty-checks ignore `_key`.
  const serialise = useCallback(
    (rs: AttrRow[]) => JSON.stringify(rs.map(({ _key, ...rest }) => rest)),
    [],
  );
  const dirty = useMemo(() => serialise(rows) !== pristine, [rows, pristine, serialise]);

  // ── Load the global list ──
  const load = useCallback(async () => {
    if (!configUrl) { setLoadError('REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'); return; }
    setLoading(true);
    setLoadError(null);
    setValidationError(null);
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const res = await fetchWithRetry(() => fetch(configUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }));
      const data = await unwrapEnvelope(res); // { version, count, rows }
      const serverRows: ServerRow[] = Array.isArray(data?.rows) ? data.rows : [];
      const mapped = serverRows.map(toAttrRow);
      setRows(mapped);
      setPristine(serialise(mapped));
      setBaseVersion(typeof data?.version === 'number' ? data.version : null);
    } catch (err: any) {
      setLoadError(err instanceof StepApiError && TRANSIENT_STATUS.has(err.status)
        ? TRANSIENT_MESSAGE
        : `Failed to load attribute list. (${err?.message ?? 'unknown error'})`);
      setRows([]);
      setPristine('[]');
      setBaseVersion(null);
    } finally {
      setLoading(false);
    }
  }, [configUrl, serialise, acquireApiToken]);

  // Load whenever the panel opens.
  useEffect(() => { if (open) load(); }, [open, load]);

  // Esc closes (only when there are no unsaved edits — avoids losing work).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dirty) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dirty, onClose]);

  // ── Row mutators ──
  const setRow = (key: string, patch: Partial<AttrRow>) =>
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const addRow = () => {
    const r: AttrRow = { _key: nextKey(), step_id: '', mapping_guidance: '', example_value: null, enabled: true };
    setRows((prev) => [...prev, r]);
    setSearch('');
  };

  const deleteRow = (key: string) => setRows((prev) => prev.filter((r) => r._key !== key));

  const move = (key: string, dir: -1 | 1) =>
    setRows((prev) => {
      const i = prev.findIndex((r) => r._key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  // ── Save (PUT — replace the whole global list) ──
  const save = async () => {
    // Validate first — instant client feedback, independent of the endpoint.
    const invalid = validate(rows);
    setValidationError(invalid);
    if (invalid) return;
    if (!configUrl) { setLoadError('REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'); return; }

    setSaving(true);
    setLoadError(null);
    try {
      const body = {
        opco: null, // global list
        updated_by: updatedBy || 'unknown',
        base_version: baseVersion,
        rows: rows.map(toServerRow),
      };
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const res = await fetchWithRetry(() => fetch(configUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      }));
      await unwrapEnvelope(res); // { saved, version } — re-GET below adopts the bump
      // Re-load to pick up the bumped version + canonical ordering.
      await load();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err: any) {
      if (err instanceof StepApiError && err.code === 'VERSION_CONFLICT') {
        const cur = err.details?.current_version;
        setLoadError(
          `The attribute list changed on the server${cur != null ? ` (now version ${cur}, you loaded ${baseVersion})` : ''}. Reload, reapply your edits, then save again — your changes are still here.`,
        );
      } else if (err instanceof StepApiError && TRANSIENT_STATUS.has(err.status)) {
        setLoadError(`${TRANSIENT_MESSAGE} Nothing was written — your changes are still here.`);
      } else {
        setLoadError(`Save failed — nothing was written. (${err?.message ?? 'unknown error'})`);
      }
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { load(); };

  // ── Derived ──
  const enabledCount = useMemo(() => rows.filter((r) => r.enabled).length, [rows]);
  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => (q === ''
      ? rows
      : rows.filter((r) =>
          r.step_id.toLowerCase().includes(q) || r.mapping_guidance.toLowerCase().includes(q))),
    [rows, q],
  );
  // Reorder needs unambiguous adjacency — only safe with no active filter.
  const canReorder = q === '';

  if (!open) return null;

  return (
    <div className="acfg-overlay" role="dialog" aria-modal="true" aria-label="Attribute List configuration">
      <div className="acfg-backdrop" onClick={() => { if (!dirty) onClose(); }} />
      <div className="acfg-panel">

        {/* ── Header ── */}
        <div className="acfg-head">
          <div className="acfg-head-left">
            <h2 className="acfg-title">Prompt Configuration</h2>
            <p className="acfg-subtitle">Global STEP attribute list injected into the extraction prompt</p>
          </div>
          <button className="acfg-close" onClick={() => { if (!dirty) onClose(); }} aria-label="Close"
            title={dirty ? 'Save or discard changes first' : 'Close'} disabled={dirty}>
            <MdClose aria-hidden="true" />
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="acfg-toolbar">
          <div className="acfg-searchbox">
            <MdSearch aria-hidden="true" className="acfg-search-icon" />
            <input
              className="acfg-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by STEP ID or guidance…"
              spellCheck={false}
              autoComplete="off"
            />
            {search && (
              <button className="acfg-search-clear" onClick={() => setSearch('')} aria-label="Clear filter">
                <MdClose aria-hidden="true" />
              </button>
            )}
          </div>

          <button className="acfg-btn acfg-btn--ghost" onClick={() => load()} disabled={loading || saving}
            title="Reload from server (discards edits)">
            <MdRefresh aria-hidden="true" />Reload
          </button>
          <button className="acfg-btn acfg-btn--add" onClick={addRow} disabled={loading || saving}>
            <MdAdd aria-hidden="true" />Add attribute
          </button>
        </div>

        {/* ── Status line ── */}
        <div className="acfg-status">
          <span className="acfg-status-item">
            Version <strong>{baseVersion ?? '—'}</strong>
          </span>
          <span className="acfg-status-sep">·</span>
          <span className="acfg-status-item">
            <strong>{rows.length}</strong> attribute{rows.length === 1 ? '' : 's'}
          </span>
          <span className="acfg-status-sep">·</span>
          <span className="acfg-status-item">
            <strong>{enabledCount}</strong> enabled
          </span>
          {q !== '' && (
            <>
              <span className="acfg-status-sep">·</span>
              <span className="acfg-status-item acfg-status-item--filter">
                showing <strong>{filtered.length}</strong> filtered
              </span>
            </>
          )}
          {dirty && <span className="acfg-dirty">● Unsaved changes</span>}
          {savedFlash && <span className="acfg-saved"><MdCheckCircle aria-hidden="true" />Saved</span>}
        </div>

        {/* ── Errors ── */}
        {loadError && (
          <div className="acfg-banner acfg-banner--error">
            <MdWarningAmber aria-hidden="true" />{loadError}
          </div>
        )}
        {validationError && (
          <div className="acfg-banner acfg-banner--warn">
            <MdWarningAmber aria-hidden="true" />{validationError.message}
          </div>
        )}

        {/* ── Grid ── */}
        <div className="acfg-grid-wrap">
          {loading ? (
            <div className="acfg-empty">Loading attribute list…</div>
          ) : rows.length === 0 ? (
            <div className="acfg-empty">
              No attributes in the global list.
              <br />Use <strong>Add attribute</strong> to start the list.
            </div>
          ) : filtered.length === 0 ? (
            <div className="acfg-empty">No attributes match "{search}".</div>
          ) : (
            <table className="acfg-grid">
              <thead>
                <tr>
                  <th className="acfg-col-sno">#</th>
                  <th className="acfg-col-en">Enabled</th>
                  <th className="acfg-col-id">STEP ID</th>
                  <th className="acfg-col-guide">Mapping Guidance</th>
                  <th className="acfg-col-ord">Order / Delete</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const realIndex = rows.findIndex((x) => x._key === r._key);
                  const isFirst = realIndex === 0;
                  const isLast = realIndex === rows.length - 1;
                  const invalidRow = validationError?.rowKey === r._key;
                  return (
                    <tr key={r._key} className={invalidRow ? 'acfg-tr--invalid' : undefined}>
                      <td className="acfg-col-sno">{realIndex + 1}</td>
                      <td className="acfg-col-en">
                        <input
                          type="checkbox"
                          className="acfg-check"
                          checked={r.enabled}
                          onChange={(e) => setRow(r._key, { enabled: e.target.checked })}
                          title={r.enabled ? 'Included in the prompt' : 'Excluded from the prompt (kept in store)'}
                        />
                      </td>
                      <td className="acfg-col-id">
                        <input
                          type="text"
                          className="acfg-id-input"
                          value={r.step_id}
                          onChange={(e) => setRow(r._key, { step_id: e.target.value })}
                          title={r.step_id || undefined}
                          placeholder="att_Name"
                          spellCheck={false}
                          autoComplete="off"
                        />
                      </td>
                      <td className="acfg-col-guide">
                        <textarea
                          className="acfg-guide-input"
                          value={r.mapping_guidance}
                          onChange={(e) => setRow(r._key, { mapping_guidance: e.target.value })}
                          placeholder="Mapping guidance for this attribute…"
                          rows={2}
                        />
                      </td>
                      <td className="acfg-col-ord">
                        <div className="acfg-ord-actions">
                          <button className="acfg-icon-btn" onClick={() => move(r._key, -1)}
                            disabled={!canReorder || isFirst}
                            title={canReorder ? 'Move up' : 'Clear filter to reorder'} aria-label="Move up">
                            <MdArrowUpward aria-hidden="true" />
                          </button>
                          <button className="acfg-icon-btn" onClick={() => move(r._key, 1)}
                            disabled={!canReorder || isLast}
                            title={canReorder ? 'Move down' : 'Clear filter to reorder'} aria-label="Move down">
                            <MdArrowDownward aria-hidden="true" />
                          </button>
                          <button className="acfg-icon-btn acfg-icon-btn--del" onClick={() => deleteRow(r._key)}
                            title="Delete row" aria-label="Delete row">
                            <MdDeleteOutline aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="acfg-footer">
          <p className="acfg-footer-hint">
            Save replaces the whole global list. Deleting a row removes that attribute from the prompt.
          </p>
          <div className="acfg-footer-actions">
            <button className="acfg-btn acfg-btn--ghost" onClick={discard} disabled={!dirty || saving || loading}>
              Discard
            </button>
            <button className="acfg-btn acfg-btn--save" onClick={save} disabled={!dirty || saving || loading}>
              <MdSave aria-hidden="true" />{saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AttributeConfig;
